import {
  Truck, FileText, Search, Package, Plus, CheckCircle, DollarSign,
  Sparkles, MessageSquare, AlertTriangle, RotateCcw, Shield, List, Wrench,
} from 'lucide-react';
import Badge from '@/components/ui/Badge';
import { formatToolExecution } from '@/lib/tool-name-mapping';
import { cn } from '@/lib/utils';

const ICON_MAP: Record<string, React.ReactNode> = {
  Truck: <Truck className="w-4 h-4" />,
  FileText: <FileText className="w-4 h-4" />,
  Search: <Search className="w-4 h-4" />,
  Package: <Package className="w-4 h-4" />,
  Plus: <Plus className="w-4 h-4" />,
  CheckCircle: <CheckCircle className="w-4 h-4" />,
  DollarSign: <DollarSign className="w-4 h-4" />,
  Sparkles: <Sparkles className="w-4 h-4" />,
  MessageSquare: <MessageSquare className="w-4 h-4" />,
  AlertTriangle: <AlertTriangle className="w-4 h-4" />,
  RotateCcw: <RotateCcw className="w-4 h-4" />,
  Shield: <Shield className="w-4 h-4" />,
  List: <List className="w-4 h-4" />,
  Wrench: <Wrench className="w-4 h-4" />,
};

interface ToolExecution {
  id: string;
  tool_name: string;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  status: string;
  duration_ms: number | null;
  created_at: string;
  error_message: string | null;
}

const statusBadge: Record<string, { variant: 'green' | 'red' | 'gray'; label: string }> = {
  success: { variant: 'green', label: 'Success' },
  error: { variant: 'red', label: 'Error' },
};

export default function ToolExecutionCard({ execution }: { execution: ToolExecution }) {
  const formatted = formatToolExecution(execution.tool_name, execution.input, execution.output);
  const badge = statusBadge[execution.status] ?? { variant: 'gray' as const, label: execution.status };
  const isError = execution.status === 'error';

  return (
    <div className={cn(
      'border rounded-lg p-3',
      isError ? 'border-red-200 bg-red-50/50' : 'border-gray-200 bg-white'
    )}>
      <div className="flex items-start gap-3">
        <div className={cn(
          'p-2 rounded-lg shrink-0',
          isError ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
        )}>
          {ICON_MAP[formatted.icon] ?? ICON_MAP['Wrench']}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{formatted.label}</span>
            <Badge variant={badge.variant}>{badge.label}</Badge>
            {execution.duration_ms != null && (
              <span className="text-xs text-gray-400">{execution.duration_ms}ms</span>
            )}
          </div>
          {formatted.detail && (
            <p className="text-sm text-gray-600 mt-0.5">{formatted.detail}</p>
          )}
          {execution.error_message && (
            <p className="text-sm text-red-600 mt-1">{execution.error_message}</p>
          )}
          <details className="mt-2">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
              Raw JSON
            </summary>
            <div className="mt-1 space-y-1">
              {execution.input && (
                <pre className="text-xs bg-gray-50 rounded p-2 overflow-x-auto text-gray-600">
                  <span className="font-semibold text-gray-500">Input: </span>
                  {JSON.stringify(execution.input, null, 2)}
                </pre>
              )}
              {execution.output && (
                <pre className="text-xs bg-gray-50 rounded p-2 overflow-x-auto text-gray-600">
                  <span className="font-semibold text-gray-500">Output: </span>
                  {JSON.stringify(execution.output, null, 2)}
                </pre>
              )}
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}
